import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ContestFee, Currency } from '@freelancer/datastore/collections';
import { ListItemInputAlignment, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight } from '@freelancer/ui/text';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';

@Component({
  selector: 'app-contest-advanced-upgrades',
  template: `
    <fl-bit [flMarginBottom]="Margin.LARGE">
      <fl-list
        [inputAlignment]="ListItemInputAlignment.TOP"
        [type]="ListItemType.CHECKBOX"
      >
        <!-- GUARANTEED -->
        <fl-list-item
          [control]="
            isUrgentUpgradeSelected
              ? disabledControl
              : control.get('guaranteed')
          "
          [flTrackingLabel]="
            control.get('guaranteed')?.value
              ? 'Guaranteed-Check'
              : 'Guaranteed-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - guaranteed upgrade
              description
            "
            [currency]="selectedCurrency"
            [price]="0"
            [upgradeType]="UpgradeType.GUARANTEED"
          >
            Guarantee freelancers that a winner will be chosen and awarded the
            prize. This will attract better entries from more freelancers.
            Moneyback guarantee is not applicable if a contest has a guaranteed
            upgrade.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- FEATURED -->
        <fl-list-item
          [control]="control.get('featured')"
          [flTrackingLabel]="
            control.get('featured')?.value
              ? 'Featured-Check'
              : 'Featured-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - featured upgrade
              description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.featuredPrice"
            [upgradeType]="UpgradeType.FEATURED"
          >
            Attract more freelancers with a prominent placement in our 'Featured
            Jobs and Contests' page.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- TOP CONTEST -->
        <fl-list-item
          [control]="control.get('topContest')"
          [flTrackingLabel]="
            control.get('topContest')?.value
              ? 'TopContest-Check'
              : 'TopContest-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - top contest upgrade
              description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.topContestPrice"
            [upgradeType]="UpgradeType.TOP_CONTEST"
          >
            We will contact our top freelancers to join your contest.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- HIGHLIGHT -->
        <fl-list-item
          [control]="control.get('highlight')"
          [flTrackingLabel]="
            control.get('highlight')?.value
              ? 'Highlight-Check'
              : 'Highlight-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - highlight upgrade
              description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.highlightPrice"
            [upgradeType]="UpgradeType.HIGHLIGHT"
          >
            Make your contest more visible in our main job search pages.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- SEALED -->
        <fl-list-item
          [control]="control.get('sealed')"
          [flTrackingLabel]="
            control.get('sealed')?.value ? 'Sealed-Check' : 'Sealed-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - sealed upgrade
              description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.sealedPrice"
            [upgradeType]="UpgradeType.SEALED"
          >
            Only you can see individual entries, ensuring you get unique ideas.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- NDA -->
        <fl-list-item
          [control]="control.get('nda')"
          [flTrackingLabel]="
            control.get('nda')?.value ? 'NDA-Check' : 'NDA-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - nda upgrade description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.ndaPrice"
            [upgradeType]="UpgradeType.NDA"
          >
            Freelancers must sign a Non-Disclosure Agreement to see your contest
            details.
          </fl-upgrade-item>
        </fl-list-item>

        <!-- PRIVATE -->
        <fl-list-item
          [control]="control.get('private')"
          [flTrackingLabel]="
            control.get('private')?.value ? 'Private-Check' : 'Private-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page contest advanced upgrade - private upgrade
              description
            "
            [currency]="selectedCurrency"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.privatePrice"
            [upgradeType]="UpgradeType.PRIVATE"
          >
            Hide project details from search engines and users that are not
            logged in, for projects that you need to keep confidential.
          </fl-upgrade-item>
        </fl-list-item>
      </fl-list>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestAdvancedUpgradesComponent implements OnInit {
  @Input() control: FormControl;
  @Input() isLoadingFees: boolean;
  @Input() isUrgentUpgradeSelected: boolean;
  @Input() selectedCurrency: Currency;
  @Input() showAdvanced: boolean;
  @Input() upgradeFees: ContestFee;

  FontWeight = FontWeight;
  ListItemInputAlignment = ListItemInputAlignment;
  ListItemType = ListItemType;
  Margin = Margin;
  UpgradeType = UpgradeType;

  disabledControl: FormControl;

  ngOnInit() {
    this.disabledControl = new FormControl(true);
    this.disabledControl.disable();
  }
}
